/**
 * 「浏览与编辑」字表 / 词表的**表头 ↔ 表尾**跳转按钮 —— 文案与滚动目标位置。
 *
 * ## 用户原话（验收标准，2026-10-20）
 *
 * > "在**字表的上面加一个按钮，直接跳转到表尾**；在**表尾那里加一个按钮，跳转到表头**。"
 *
 * 于是：
 *   · 列表**上方**一颗「跳到表尾 ↓」；
 *   · 列表**末尾**一颗「跳到表头 ↑」；
 *   · 只出现在「浏览与编辑」的字表 / 词表区（⛔ 导入预览那边不加）。
 *
 * ## 为什么滚动目标要在这里算（而不是直接 `scrollIntoView`）
 *
 * 直接 `element.scrollIntoView()` 会把列表顶对齐到视口最上面，被 AppShell 的
 * **吸顶 header**（手机上还有**吸底 tab 栏**）盖住一截。所以按元素矩形 + 视口算出
 * `window.scrollTo({ top })`：
 *
 *   · 表头：`列表顶 - 吸顶 header 高度 - 一点余量`；
 *   · 表尾：`列表底 - 视口高 + 吸底栏高度 + 一点余量`（让最后一行露在吸底栏上方）。
 *
 * 纯函数（矩形 / 视口 / 边）⇒ 不需要 DOM 就能测；组件只负责把真实数字喂进来。
 * 平滑 vs 瞬时由 `hanziJump.ts` 的 `prefersReducedMotion()` +
 * `hanziJumpScrollBehavior()` 决定（与"字卡跳转"共用同一份 reduced-motion 判据）。
 */

/** 列表上方那颗按钮的文案（逐字，用户原话）。 */
export const BROWSE_JUMP_TO_BOTTOM_LABEL = '跳到表尾 ↓';

/** 列表末尾那颗按钮的文案（逐字，用户原话）。 */
export const BROWSE_JUMP_TO_TOP_LABEL = '跳到表头 ↑';

/** 跳到哪一头。 */
export type BrowseScrollEdge = 'top' | 'bottom';

/**
 * 吸顶 header 的让位（px）。
 *
 * AppShell 的手机 / 桌面 header 都是 `sticky top-0`，实测高度 56~64px；留 72 =
 * header + 8px 余量，列表第一行不会贴着它。
 */
export const BROWSE_SCROLL_TOP_MARGIN_PX = 72;

/**
 * 吸底 tab 栏的让位（px）。
 *
 * 手机上 AppShell 底部是 `fixed` 的 tab 栏（约 64px + safe-area）；留 88 =
 * 64 + 24px 余量，滚到"最底部"时最后一行不会被它挡住。
 */
export const BROWSE_SCROLL_BOTTOM_MARGIN_PX = 88;

/** 元素矩形（`getBoundingClientRect()` 里真正要用的两项）。 */
export interface BrowseScrollRect {
  top: number;
  bottom: number;
}

/** 视口（`window.scrollY` / `window.innerHeight`）。 */
export interface BrowseScrollViewport {
  scrollY: number;
  innerHeight: number;
}

/**
 * 跳到表头 / 表尾时 `window.scrollTo({ top })` 的目标值。
 *
 * @param rect     列表容器的 `getBoundingClientRect()`
 * @param viewport  当前 `scrollY` 与 `innerHeight`
 * @param edge     `'top'` = 列表顶对齐（让开吸顶 header）；`'bottom'` = 列表底对齐
 *                 （让开吸底 tab 栏）
 *
 * 结果夹到 `>= 0`（浏览器本来也会夹，这里让测试能断言一个确定值）。
 */
export function browseScrollTarget(
  rect: BrowseScrollRect,
  viewport: BrowseScrollViewport,
  edge: BrowseScrollEdge,
): number {
  const absolute =
    edge === 'top'
      ? viewport.scrollY + rect.top - BROWSE_SCROLL_TOP_MARGIN_PX
      : viewport.scrollY + rect.bottom - viewport.innerHeight + BROWSE_SCROLL_BOTTOM_MARGIN_PX;
  return Math.max(0, Math.round(absolute));
}
